/// FOURJS_START_COPYRIGHT(D,2026)
/// Property of Four Js*
/// (c) Copyright Four Js 2026, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("OpenAIProvider", ["AIProviderBase"], function (context, cls) {
  /**
   * Open AI provider
   * @class OpenAIProvider
   * @memberOf classes
   */
  class OpenAIProvider extends cls.AIProviderBase /** @lends classes.OpenAIProvider.prototype */ {
    constructor() {
      super("openai", "OpenAI", "remote", { rewrite: true, conversation: true });
    }

    /**
     * @inheritdoc
     */
    _getDirectApiHost() {
      return "https://api.openai.com";
    }

    /**
     * Base URL for OpenAI Response API
     * @returns {string}
     * @private
     */
    _getBaseUrl() {
      return this._buildApiUrl("v1/responses");
    }

    /**
     * Rewrite text using OpenAI Responses API
     * @inheritDoc
     */
    async rewrite(text, options = {}) {
      const preparedText = this._prepareRewriteInput(text);
      if (preparedText === null) {
        return null;
      }

      const instructions = this._buildRewritePrompt();
      const input = this._buildRewriteInput(preparedText, options);

      const data = await this._request(this._getBaseUrl(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this._resolveApiKey(options.apiKey)}`
        },
        body: {
          model: this._getModel("rewrite"),
          instructions,
          input
        }
      });

      return this._extractOutputText(data);
    }

    /**
     * Extract output text from OpenAI Responses API payload
     * @param {Object} response
     * @returns {string|null}
     * @private
     */
    _extractOutputText(response) {
      if (!response || !Array.isArray(response.output)) {
        return null;
      }

      return response.output
        .filter((item) => item.type === "message" && Array.isArray(item.content))
        .flatMap((item) => item.content)
        .filter((part) => part.type === "output_text" && typeof part.text === "string")
        .map((part) => part.text)
        .join("");
    }

    /**
     * Base URL for OpenAI Conversations API
     * @returns {string}
     * @private
     */
    _getConversationBaseUrl() {
      return this._buildApiUrl("v1/conversations");
    }

    /**
     * @inheritdoc
     */
    async createConversation(options = {}) {
      const data = await this._request(this._getConversationBaseUrl(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this._resolveApiKey(options.apiKey)}`
        },
        body: {}
      });

      if (!data?.id) {
        return null;
      }

      return { conversationId: data.id };
    }

    /**
     * @inheritdoc
     */
    async sendMessage(state, userMessage, options = {}) {
      const data = await this._request(this._getBaseUrl(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this._resolveApiKey(options.apiKey)}`
        },
        body: {
          model: this._getModel("conversation"),
          conversation: state.providerData.conversationId,
          input: userMessage
        }
      });

      return { providerData: state.providerData, response: this._extractOutputText(data) };
    }

    /**
     * @inheritdoc
     */
    async deleteConversation(state, options = {}) {
      await this._request(`${this._getConversationBaseUrl()}/${state.providerData.conversationId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${this._resolveApiKey(options.apiKey)}`
        }
      });
    }
  }

  cls.AIProviderBase.registerProvider(OpenAIProvider);
  cls.OpenAIProvider = OpenAIProvider;
});
